import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { EditorTool, SimpleEditor, ToolbarButton, useElfeEditor } from '@follow/elfe'
import {
  Button,
  ClickableLink,
  DropdownItem,
  Input,
  MultiSelectInput,
  Switch,
  TooltipWrapper,
  ValidationModal,
} from '../../shared'
import LogoMsSante from '../../../assets/images/logo_mssante.png'
import { MailEditorProps } from './MailEditor.model'
import { emailRegex } from '../../../misc/regex'
import { LightPatient } from '../../../model/Patient'
import { useDebounce } from 'react-use'
import { MultiSelectOption } from '../../../model/SelectOption'
import { MessageTemplateManager } from '../MessageTemplateManager'
import { useGetDefaultMessageTemplate } from '../../../hooks/queries/messageTemplates/useGetDefaultMessageTemplate.query'
import { isHtmlEmptyValue } from '../../../misc/html.utils'
import { useAllowPatientsMail, useSendMail } from '../../../hooks/queries/mail'
import { BottomPanel } from '../../shared/bottomPanel/BottomPanel'
import { MailEditorAttachments } from './MailEditorAttachments'
import { useUserEnabledFeature } from '../../../hooks/utils/user'
import { FeaturesType } from '@follow/cdk'
import { isDefined } from '../../../misc/functions.utilities'
import { useRenderPdfFromDocuments } from '../../../hooks/queries/document/useRenderPdfFromDocuments.query'

import { useActionDispatch } from '../../../hooks/utils'
import { addValid } from '../../../store/message'
import { useGetRecipientSuggestion } from '../../../hooks/queries/recipientSuggestions'
import { ContactMssEmail, ContactMssEmailOption } from '../../../model/ContactMssEmail'
import { ContactMssEmailDropdownItem } from '../../contactMssEmail'
import { mapContactMssEmailToContactMssEmailOption } from '../../../misc/contactMssEmail.utilities'
import { useContactMssEmailOptions } from '../../../hooks/utils/mail/useContactMssEmailOptions.hook'
import {
  areRecipientsEquals,
  getDocumentsToRender,
  getRenderableDocumentsVariable,
  isFileAlreadyAttachedToEmail,
} from './MailEditor.utils'
import { SendableDocument } from '../../../model/DocumentInstance'
import { useFetchFileForMail } from './useFetchFileForMail.query'
import { getPatientMssEmail } from '../../../misc/patient.utilities'
import InsUtilities from '../../../features/ins/utilities/Ins.utilities'
import { FwTrackingEvent, TrackingService } from '../../../misc/Tracking'
import { useCreateResourceHistory } from '../../../hooks/queries/resourceHistory'
import { isDoctor } from '../../../misc/user.utilities'
import { ConnectedUserContext } from '../../../misc/auth.utilities'
import { ResourceHistoryMutationPayload } from '../../../data/resourceHistory/mapper'

const editorTools: Array<EditorTool> = [
  'caseSwap',
  'heading',
  'fontFamily',
  'fontSize',
  'marks',
  'colors',
  'align',
  'list',
  'lineHeight',
  'unformat',
  'undo',
  'table',
]

const EMPTY_CONTACT_MSS_EMAIL_ID = '0'
const emptyContactMssEmail: ContactMssEmail = {
  uuid: EMPTY_CONTACT_MSS_EMAIL_ID,
  mssEmailAddress: '',
  contact: {
    uuid: '',
    title: '',
    firstName: '',
    familyName: '',
    profession: null,
    speciality: null,
    organizations: [],
    private: false,
  },
}

const getEmailOption = (emailAddress: string): MultiSelectOption<ContactMssEmailOption> => ({
  label: emailAddress,
  value: mapContactMssEmailToContactMssEmailOption({
    ...emptyContactMssEmail,
    mssEmailAddress: emailAddress,
  }),
})

const FILE_ADD_TOAST_ID = 'file_add_from_mail_editor'

export const MailEditor: FC<MailEditorProps> = ({
  isUnmounting,
  initialRecipients,
  initialContent,
  initialSubject,
  initialPatient,
  initialDocuments,
  initialFileIds,
  replyReferences,
  replyToId,
  medicalEventId,
  onClose,
}) => {
  const { currentUser } = useContext(ConnectedUserContext)
  const [localMailSubject, setLocalMailSubject] = useState(initialSubject ?? '')
  const [addressInput, setAddressInput] = useState('')
  const [selectedPatient, setSelectedPatient] = useState<LightPatient | undefined>(initialPatient)
  const [emailAddresses, setEmailAddresses] = useState<MultiSelectOption<ContactMssEmailOption>[]>(
    initialRecipients?.map(getEmailOption) ?? [],
  )

  const [displayCloseModal, setDisplayCloseModal] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [selectedDocument, setSelectedDocument] = useState<SendableDocument[]>(
    initialDocuments || [],
  )

  const [replyToEmail, setReplyToEmail] = useState<string>()
  const [askReadAcknowledgement, setAskReadAcknowledgement] = useState(false)
  const [openedMessageTemplateManager, setOpenedMessageTemplateManager] = useState(false)
  const [recipientSuggestionSearch, setRecipientSuggestionSearch] = useState('')

  const { mutate: createResourceHistory } = useCreateResourceHistory()
  const {
    query: { data: defaultMessageTemplate },
  } = useGetDefaultMessageTemplate({})
  const {
    query: { data: recipientSuggestion },
    cancelPendingQuery: cancelRecipientSuggestionSearchQuery,
  } = useGetRecipientSuggestion({
    search: recipientSuggestionSearch,
    patientId: selectedPatient?.id,
  })
  const isMailAttachmentsEnabled = useUserEnabledFeature(FeaturesType.mailAttachments)
  const vitalCardService = useUserEnabledFeature(FeaturesType.vitalCardService)
  const stellairInsiCheck = useUserEnabledFeature(FeaturesType.stellairInsiCheck)

  const dispatchValid = useActionDispatch(addValid)
  const { mutate: sendMail, isPending: isSendMailLoading } = useSendMail()

  const { render } = useRenderPdfFromDocuments({
    onSuccess: (rendered) => {
      const newFile = new File([rendered.blob], rendered.fileName || 'Document')

      if (!isFileAlreadyAttachedToEmail(selectedFiles, newFile)) {
        setSelectedFiles((current) => [...current, newFile])
      }
    },
  })

  const handleAddSendableDocuments = useCallback(
    (documents: Array<SendableDocument>) => {
      if (documents.length === 0) return

      dispatchValid("Documents en cours d'ajout", undefined, { id: FILE_ADD_TOAST_ID })

      const newDocuments = [...selectedDocument, ...documents]
      setSelectedDocument(newDocuments)

      const renderables = getDocumentsToRender(documents)
      renderables.forEach((doc) => render(getRenderableDocumentsVariable(doc)))
    },
    [dispatchValid, selectedDocument, render],
  )

  const { mutate: addFileToAttachments } = useFetchFileForMail({
    onSuccess: (file) => {
      if (!isFileAlreadyAttachedToEmail(selectedFiles, file)) {
        setSelectedFiles((current) => [...current, file])
      }
    },
  })

  const selectedEmailAddresses = useMemo(
    () => emailAddresses.map(({ value }) => value.mssEmailAddress),
    [emailAddresses],
  )
  const { blackListedPatients, unqualifiedPatients } = useAllowPatientsMail({
    emails: selectedEmailAddresses,
  })

  const handleAddFiles = useCallback(
    (fileIds: Array<number>) => {
      if (fileIds.length === 0) return

      dispatchValid("Documents en cours d'ajout", undefined, { id: FILE_ADD_TOAST_ID })

      fileIds.forEach((id) => addFileToAttachments({ fileId: id }))
    },
    [addFileToAttachments, dispatchValid],
  )

  const initialDocumentsHaveBeenRendered = useRef(false)
  useEffect(() => {
    if (initialDocumentsHaveBeenRendered.current) return

    initialDocumentsHaveBeenRendered.current = true

    if (initialDocuments) {
      handleAddSendableDocuments(initialDocuments)
    }
    if (initialFileIds) {
      handleAddFiles(initialFileIds)
    }
  }, [handleAddFiles, handleAddSendableDocuments, initialDocuments, initialFileIds])

  const { contactMssEmailGroups, contactMssEmailOptions } = useContactMssEmailOptions(
    recipientSuggestion,
    addressInput,
    emptyContactMssEmail,
  )

  const formError = useMemo(() => {
    if (emailAddresses.length === 0) {
      return 'Veuillez saisir une adresse mail valide'
    }

    if (
      selectedPatient?.inseeNumber &&
      selectedPatient.disallowCitizenMSSMessaging &&
      emailAddresses.some(
        (email) => email.value.mssEmailAddress === getPatientMssEmail(selectedPatient.inseeNumber!),
      )
    ) {
      return 'Le patient refuse le partage de ses informations vers sa MSS Citoyenne'
    }

    if (
      selectedPatient?.inseeNumber &&
      selectedPatient.disallowProfessionalMSSMessaging &&
      (emailAddresses.length > 1 ||
        emailAddresses.at(0)?.value.mssEmailAddress !==
          getPatientMssEmail(selectedPatient.inseeNumber!))
    ) {
      return 'Le patient refuse le partage de ses informations via MSS Professionelle'
    }

    if (replyToEmail && !emailRegex.test(replyToEmail)) {
      return 'Veuillez saisir une adresse mail de retour valide'
    }

    if (blackListedPatients.length > 0) {
      const base =
        blackListedPatients.length === 1
          ? "Un patient refuse d'être contacté"
          : "Plusieurs patients saisis refusent d'être contactés"
      return `${base} via la messagerie sécurisée`
    }

    const bypassCheckForStellairUsers = vitalCardService === 'stellair' && !stellairInsiCheck
    if (unqualifiedPatients.length > 0 && !bypassCheckForStellairUsers) {
      const patientBlock = unqualifiedPatients.length < 2 ? 'du patient' : 'des patients'
      return `L'identité ${patientBlock} doit être qualifiée`
    }

    if (selectedPatient && selectedFiles.length === 0) {
      return `Vous ne pouvez pas envoyer de mail à propos d'un patient sans ajouter de pièce jointe`
    }

    return null
  }, [
    blackListedPatients.length,
    emailAddresses,
    replyToEmail,
    selectedFiles.length,
    selectedPatient,
    stellairInsiCheck,
    unqualifiedPatients.length,
    vitalCardService,
  ])

  const { editor, config } = useElfeEditor(
    {
      editorType: 'simple',
      tools: editorTools,
      placeholder: 'Écrivez votre message ici...',
    },
    {
      content: initialContent ?? '',
    },
  )

  useEffect(() => {
    if (!editor || !isHtmlEmptyValue(editor.getHTML())) return
    if (!defaultMessageTemplate) return

    editor.commands.setContent(defaultMessageTemplate.body)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, defaultMessageTemplate])

  useDebounce(
    () => {
      setRecipientSuggestionSearch(addressInput)
    },
    500,
    [addressInput],
  )

  const handleAddressInputChange = useCallback(
    (value: string) => {
      setAddressInput(value)
      cancelRecipientSuggestionSearchQuery()
    },
    [cancelRecipientSuggestionSearchQuery],
  )

  const handleSelectEmailAddress = useCallback(
    (value: MultiSelectOption<ContactMssEmailOption>[]) => {
      const filtered = value.filter(
        (item) => item.value.mssEmailAddress && emailRegex.test(item.value.mssEmailAddress),
      )

      setEmailAddresses(filtered)

      // Si l'email en cours de saisie est valide, on vide l'input text
      if (value.length === filtered.length) {
        setAddressInput('')
      }
    },
    [],
  )

  const handleBackToEditorModal = useCallback(() => {
    setDisplayCloseModal(false)
  }, [])

  const handleSend = useCallback(() => {
    const recipients = emailAddresses.map(({ value }) => value.mssEmailAddress)
    const htmlContent = editor?.getHTML()
    const html = `<div>${htmlContent}</div>`
    const replyTo = replyToEmail && emailRegex.test(replyToEmail) ? replyToEmail : undefined

    const isQualified = selectedPatient && InsUtilities.isQualified(selectedPatient)
    sendMail(
      {
        payload: {
          subject: localMailSubject,
          content: html,
          recipientEmails: recipients,
          files: selectedFiles,
          documentIds: [...new Set(selectedDocument.map((doc) => doc.id))],
          askReadAcknowledgement,
          replyToEmail: replyTo,
          patientId: selectedPatient?.id,
          references: replyReferences ?? null,
          inReplyTo: replyToId ?? null,
        },
      },
      {
        onSuccess: () => {
          // log mss
          try {
            if (isQualified && isDoctor(currentUser) && currentUser?.mailizEmailAddress) {
              const { birthFirstName, birthLastName, otherFirstNames, birthDate, birthPlaceCode } =
                selectedPatient

              const sendFiles = selectedFiles?.map((file) => {
                return {
                  id: '0',
                  title: file.name,
                  type: 'file',
                }
              }).filter((file, index, array) =>
                array.findIndex(f => f.title === file.title) === index
              )

              const sendDocuments = selectedDocument?.map((doc) => {
                return {
                  id: `${doc.id}`,
                  title: doc.title,
                  type: 'doc',
                }
              }).filter((doc, index, array) =>
                array.findIndex(d => d.id === doc.id) === index
              )


              const ressourceHistory: ResourceHistoryMutationPayload = {
                eventType: 'mss_email_sent',
                resourceType: 'Patient',
                resourceId: selectedPatient?.id.toString() ?? '',
                extraData: {
                  inseeNumber: selectedPatient?.inseeNumber ?? '',
                  birthFirstName: birthFirstName ?? '',
                  birthLastName: birthLastName ?? '',
                  otherFirstNames: otherFirstNames ?? '',
                  birthDate: birthDate ?? '',
                  birthPlaceCode: birthPlaceCode ?? '',
                  sender: currentUser?.mailizEmailAddress,
                  recipients: (emailAddresses ?? []).map((recipient) => ({
                    email: recipient.value.mssEmailAddress,
                    firstname: recipient.value.firstName,
                    lastname: recipient.value.familyName,
                  })),
                  documents: [...sendFiles, ...sendDocuments],
                },
              }

              createResourceHistory(ressourceHistory)
            }
          } catch (error) {
            console.error(error)
          }

          onClose()
          if (medicalEventId) {
            TrackingService.sendEvent(FwTrackingEvent.MSS_MEDICAL_EVENT, {
              medicalEventId,
            })
          }
        },
      },
    )
  }, [
    emailAddresses,
    editor,
    replyToEmail,
    sendMail,
    localMailSubject,
    selectedFiles,
    selectedDocument,
    askReadAcknowledgement,
    selectedPatient,
    replyReferences,
    replyToId,
    onClose,
    medicalEventId,
    currentUser,
    createResourceHistory,
  ])

  const handleConfirmClose = useCallback(() => {
    setDisplayCloseModal(false)
    onClose()
  }, [onClose])

  const handleClose = useCallback(() => {
    const isEditorEmpty = editor?.getText() === ''
    const isSubjectEmpty = localMailSubject.length === 0
    const isEmailEmpty = emailAddresses.length === 0
    const isPatientEmpty = selectedPatient === undefined

    const isFormEmpty = isEditorEmpty && isSubjectEmpty && isEmailEmpty && isPatientEmpty

    if (isFormEmpty) {
      handleConfirmClose()
    } else {
      setDisplayCloseModal(true)
    }
  }, [editor, emailAddresses.length, handleConfirmClose, localMailSubject.length, selectedPatient])

  const lockSendButton = !!formError && !isSendMailLoading

  return (
    <>
      <BottomPanel
        testId="email-create"
        isUnmounting={isUnmounting}
        onRequestClose={handleClose}
        title="Nouveau message"
        displayCloseButton={false}
        actions={
          <>
            <Button label="Annuler" theme="dark" onClick={handleClose} />
            <TooltipWrapper display={lockSendButton} content={formError} size="small">
              <div>
                <Button
                  type="submit"
                  label={isSendMailLoading ? 'Envoi en cours...' : 'Envoyer le message'}
                  theme="primary"
                  disabled={lockSendButton}
                  onClick={handleSend}
                />
              </div>
            </TooltipWrapper>
          </>
        }
      >
        <div className="flex flex-col justify-between h-full px-2 overflow-x-hidden overflow-y-scroll bg-white">
          <div className="px-4 pt-2 pb-4 space-y-2">
            <div className="flex items-center">
              <MultiSelectInput
                title="Destinataire(s)"
                mode="multiline"
                placeholder="Rechercher un destinataire"
                value={emailAddresses}
                options={contactMssEmailOptions}
                optionGroups={contactMssEmailGroups}
                areElementsEquals={areRecipientsEquals}
                disabled={isSendMailLoading}
                valid={addressInput.length > 0 ? emailRegex.test(addressInput) : undefined}
                emitChange={handleAddressInputChange}
                onSelect={handleSelectEmailAddress}
                renderOption={({ value }, _, isHovered) =>
                  value.isEmptyContact ? (
                    <DropdownItem selected={isHovered}>
                      <span className="mx-2">{value.mssEmailAddress}</span>
                    </DropdownItem>
                  ) : (
                    <ContactMssEmailDropdownItem
                      contactMssEmailOption={value}
                      selected={isHovered}
                    />
                  )
                }
                testId="multi_select-mss-email-recipients"
              />
              <div className="ml-4 font-medium w-80 text-shades-2">
                <Switch
                  checked={askReadAcknowledgement}
                  onChange={setAskReadAcknowledgement}
                  name="Accusé de réception"
                  testId="switch-read_acknowledgement-ask"
                />
              </div>
              <span className="ml-8 mr-2 text-base font-bold text-shades-2 whitespace-nowrap">
                Envoyé via
              </span>
              <img src={LogoMsSante} width="52px" height="auto" alt="logo-mss" />
            </div>
            {replyToEmail === undefined ? (
              <div className="ml-2">
                <ClickableLink
                  label="Ajouter une adresse de retour différente"
                  icon="add"
                  onClick={() => setReplyToEmail('')}
                />
              </div>
            ) : (
              <Input
                label="Adresse de retour"
                name="mailToAddress"
                placeholder="Entrer une adresse de retour"
                colorPreset="light"
                valid={replyToEmail.length > 0 ? emailRegex.test(replyToEmail) : undefined}
                error={
                  replyToEmail.length > 0 && !emailRegex.test(replyToEmail)
                    ? "L'adresse saisie n'est pas valide"
                    : undefined
                }
                disabled={isSendMailLoading}
                value={replyToEmail}
                onChange={(event) => setReplyToEmail(event.target.value)}
              />
            )}
            <Input
              label="Objet"
              name="object"
              colorPreset="light"
              value={localMailSubject}
              disabled={isSendMailLoading}
              onChange={(event) => setLocalMailSubject(event.target.value)}
            />
          </div>

          {isMailAttachmentsEnabled && (
            <MailEditorAttachments
              disabled={isSendMailLoading}
              disabledPatientSelect={isDefined(initialPatient)}
              selectedPatient={selectedPatient}
              selectedFiles={selectedFiles}
              onSelectPatient={setSelectedPatient}
              setSelectedFiles={setSelectedFiles}
              onAddDocuments={handleAddSendableDocuments}
              onAddFiles={handleAddFiles}
            />
          )}
          <SimpleEditor
            editor={editor}
            config={config}
            toolbarTop={0}
            extraTools={
              <ToolbarButton
                title="Modèles de message"
                label="Modèles de message"
                onClick={() => setOpenedMessageTemplateManager(true)}
                testId="button-toolbar-editor-message_template"
              />
            }
          />
        </div>
      </BottomPanel>
      <ValidationModal
        display={displayCloseModal}
        title="Votre saisie ne sera pas sauvegardée, voulez-vous fermer l'éditeur de message ?"
        onSubmit={handleConfirmClose}
        onClose={handleBackToEditorModal}
        testId="modal-mail_editor-close"
      />
      <MessageTemplateManager
        display={openedMessageTemplateManager}
        onClose={() => setOpenedMessageTemplateManager(false)}
        editor={editor}
      />
    </>
  )
}
